import Head from "next/head";
import Link from "next/link";
import dynamic from "next/dynamic";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

dataCenterReadiness.title = "Data Centre Project Readiness & Delivery Governance | ITMTB";
dataCenterReadiness.desc =
  "Independent technology-side readiness, risk, vendor, approval, and delivery-governance support before and during data centre setup. Fixed-scope readiness assessment with risk register, approval dependency map, work-package map, and executive decision pack.";
dataCenterReadiness.header_img = "https://www.itmtb.com/services/capability/automate.webp";
dataCenterReadiness.keywords =
  "data center readiness assessment India, data centre project governance, data center delivery governance, captive data center planning, disaster recovery site readiness, data center risk register, owner side technology representative, data center approval dependency map, data center PMO governance India";

const faqItems = [
  {
    q: "Is ITMTB a data centre EPC contractor?",
    a: "No. ITMTB is not positioning this service as EPC, turnkey construction, or engineering delivery. This service focuses on readiness assessment, technology-side governance, project risk visibility, vendor and workstream coordination, and executive decision support. Where engineering or statutory sign-off is required, it must come from qualified specialist partners.",
  },
  {
    q: "Do you provide MEP or electrical engineering sign-off?",
    a: "No. Licensed engineering sign-off must come from qualified engineers or specialist consultants. ITMTB can help define the questions that need answering, review assumptions, coordinate workstreams, and support the owner-side governance layer around those specialists.",
  },
  {
    q: "Do you handle data centre approvals?",
    a: "We do not claim to replace local counsel, regulatory consultants, fire consultants, DISCOM coordination, or statutory approval specialists. We help map approval dependencies, identify ownership gaps, and track approval-related risks as part of project governance. Final validation should be done with the relevant authorities and licensed partners.",
  },
  {
    q: "Can you help before we choose a site?",
    a: "Yes. That is one of the best times to engage. A readiness assessment can help identify the site, power, water, telecom, approval, vendor, and operations questions that should be answered before the project moves further and becomes difficult to control.",
  },
  {
    q: "Can you help if we already have EPC or MEP partners?",
    a: "Yes. ITMTB can support the owner-side governance layer by helping track risks, assumptions, dependencies, decisions, vendor interfaces, reporting cadence, and handover readiness. We do not replace the engineering partner. We help the owner govern the work more clearly.",
  },
  {
    q: "What does the 2 to 3 week readiness assessment include?",
    a: "The assessment typically includes requirements review, assumption mapping, approval dependency mapping, capacity and availability review, a risk register, an indicative work-package map, a delivery roadmap, and an executive decision pack. The exact scope is agreed during discovery.",
  },
  {
    q: "Can you manage the full data centre project end to end?",
    a: "Not as a standalone EPC or turnkey project-management claim. We can support readiness, governance, coordination, and owner-side technology representation. Full delivery should involve qualified engineering, EPC, regulatory, and certification partners.",
  },
  {
    q: "Is this useful for investors or boards?",
    a: "Yes. The service is useful when decision-makers need a structured view of project readiness, assumptions, risks, vendor dependencies, approval dependencies, and next-stage decisions before approving investment or execution.",
  },
];
dataCenterReadiness.faq = faqItems;

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "name": "Data Centre Project Readiness and Delivery Governance",
      "provider": {
        "@type": "Organization",
        "name": "ITMTB",
        "url": "https://www.itmtb.com",
        "logo": "https://www.itmtb.com/logo.webp",
        "sameAs": [
          "https://www.linkedin.com/company/itmtb-technologies/",
          "https://x.com/ITMTBTech",
        ],
      },
      "areaServed": { "@type": "Country", "name": "India" },
      "serviceType": "Data Centre Project Readiness and Delivery Governance",
      "description":
        "Independent technology-side readiness, risk, vendor, approval, and delivery-governance support before and during data centre setup. ITMTB does not provide EPC, turnkey construction, licensed engineering design, or certification delivery.",
      "offers": {
        "@type": "Offer",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "description": "Fixed-scope 2 to 3 week readiness assessment, then custom proposal based on scope",
        },
      },
    },
  ],
};

const includeAreas = [
  {
    img: null,
    key: "Requirements and Assumptions Review",
    title: "Separate confirmed requirements from assumptions before they drive expensive decisions.",
    text: "We review the business and technology assumptions behind the project: business objective, expected capacity, IT load assumptions, availability expectations, security and access expectations, growth assumptions, internal stakeholder expectations, known constraints, and unresolved decisions.\n\nThe goal is to separate what is confirmed from what is assumed, so the project moves forward on a clear basis rather than an optimistic one.",
  },
  {
    img: null,
    key: "Site and Infrastructure Readiness Questions",
    title: "The questions that must be answered before a site or facility plan is treated as ready.",
    text: "We do not provide land, civil, or engineering sign-off. We help identify the questions that must be answered first: power availability, cooling and water, telecom and fibre, access and logistics, flood, heat and climate risk, future expansion, and operations access and maintainability.\n\nSpecialist review, where required, should be performed by qualified engineering, legal, fire, electrical, environmental, or regulatory partners.",
  },
  {
    img: null,
    key: "Approval Dependency Map",
    title: "See which approvals, NOCs, and utility dependencies can affect site, construction, energisation, and handover.",
    text: "Data centre approvals are local, state-specific, and project-specific. We help create an approval dependency map covering land use and zoning, building plan approval, fire NOC, power connection, DG and fuel dependencies, water and sewerage, environmental or pollution-control dependencies, telecom right-of-way, safety and labour requirements, and state incentives where relevant.\n\nThis is not legal advice. Final validation should be done with local counsel, regulatory consultants, licensed engineers, fire consultants, DISCOM, and relevant authorities.",
  },
  {
    img: null,
    key: "Capacity and Availability Needs",
    title: "Clarify capacity and availability intent before vendors start designing around unclear goals.",
    text: "We help the sponsor clarify day-one capacity, future capacity, rack-density assumptions, business continuity expectations, redundancy expectations, maintenance expectations, certification intent if any, uptime and resilience expectations, and operations and monitoring expectations.\n\nThe output is not a technical design. It is a clearer decision basis for the design team and project sponsors.",
  },
  {
    img: null,
    key: "Vendor and Work-Package Map",
    title: "Understand where vendor gaps and interface risks are likely to appear.",
    text: "Data centre projects involve several specialist vendors and package boundaries. We help map likely work packages and vendor dependencies across civil and structural, MEP, electrical systems, UPS and batteries, DG and fuel systems, cooling, fire and life safety, racks and containment, telecom and fibre, BMS, DCIM and EPMS, physical security, commissioning, and operations and maintenance.\n\nThis helps leadership see where vendor gaps and interface risks may appear before contracts are signed.",
  },
  {
    img: null,
    key: "Delivery Risk Register",
    title: "A practical risk register leadership can actually use for decision-making.",
    text: "We create a risk register grouped across site risk, power risk, cooling and water risk, approval risk, vendor risk, procurement risk, commissioning risk, operations handover risk, cost and schedule risk, and stakeholder decision risk.\n\nEach risk is written with owner, impact, probability, mitigation, dependency, and next action, so it drives decisions rather than sitting in a slide deck.",
  },
  {
    img: null,
    key: "Delivery Roadmap",
    title: "Convert findings into a sequenced roadmap of decisions, reviews, and stage gates.",
    text: "The readiness assessment converts findings into a sequenced roadmap: decisions needed before site commitment, specialist reviews needed, approval dependencies to validate, vendor packages to prepare, workstreams to launch, governance structure required, stage gates, executive decisions, and next 30, 60, and 90 day actions.\n\nThe roadmap gives the project a clear order of operations instead of parallel workstreams that collide later.",
  },
  {
    img: null,
    key: "Executive Decision Pack",
    title: "A written decision document for sponsors, boards, and investors.",
    text: "The final output is written for decision-makers. It usually includes a current readiness summary, major assumptions, top risks, approval dependency view, vendor and work-package map, recommended next steps, decision gates, open questions, roadmap, and suggested governance model.\n\nThe goal is to help leadership decide whether to proceed, pause, revise assumptions, appoint specialist partners, or structure the next phase differently.",
  },
];

const engagementPhases = [
  {
    phase: "01",
    title: "Discovery and Context",
    items: [
      "Understand the business objective and facility intent",
      "Identify known constraints and stakeholders",
      "Review existing documents and vendor conversations",
      "Clarify the decision timeline the assessment must support",
    ],
  },
  {
    phase: "02",
    title: "Document and Assumption Review",
    items: [
      "Review concept notes, site assumptions, and capacity estimates",
      "Review vendor inputs, approval notes, and diagrams",
      "Separate confirmed requirements from assumptions",
      "Identify documents that must be created before the next stage",
    ],
  },
  {
    phase: "03",
    title: "Workstream Mapping",
    items: [
      "Map site, power, cooling, telecom, and civil workstreams",
      "Map approvals, vendors, and commissioning dependencies",
      "Map security and operations-readiness workstreams",
      "Expose cross-workstream dependencies early",
    ],
  },
  {
    phase: "04",
    title: "Risk and Gap Analysis",
    items: [
      "Identify main assumptions and unresolved decisions",
      "Identify project, vendor, and approval risks",
      "Convert each risk into an owner, mitigation, and next action",
      "Flag where specialist validation is required",
    ],
  },
  {
    phase: "05",
    title: "Executive Decision Pack",
    items: [
      "Present a written decision pack for leadership",
      "Show whether the project is ready to proceed",
      "Show what needs specialist validation and sequencing",
      "Recommend a governance model for the next phase",
    ],
  },
];

const engagementModels = [
  {
    label: "Readiness Assessment",
    duration: "2 to 3 weeks · Fixed scope",
    description: "A focused assessment for leadership teams that need clarity before making major data centre commitments.",
    bestFor: [
      "Early feasibility and pre-site commitment",
      "Pre-vendor appointment",
      "Board or investor review",
      "DR or captive data centre planning",
      "Vendor brief preparation",
    ],
  },
  {
    label: "Governance Setup",
    duration: "2 to 4 weeks",
    description: "A structured setup engagement for organisations that already have a direction but need the governance layer defined before execution.",
    bestFor: [
      "PMO setup and RACI definition",
      "Approval tracker and workstream governance",
      "Risk, issue, and decision log setup",
      "Reporting cadence and stage-gate design",
      "Change-control structure",
    ],
  },
  {
    label: "Delivery Governance Retainer",
    duration: "Monthly retainer",
    description: "Ongoing owner-side governance support during planning, vendor selection, procurement, commissioning preparation, and handover readiness.",
    bestFor: [
      "Sponsors without a mature internal data centre PMO",
      "Organisations coordinating multiple vendors",
      "Teams that need independent risk visibility",
      "Projects moving from assessment to delivery",
      "Leadership needing structured reporting and escalation",
    ],
  },
  {
    label: "Owner's Technology Representative",
    duration: "Monthly or milestone-based",
    description: "A technology-side representative role for sponsors who need help challenging assumptions and keeping technology, operations, vendors, and governance aligned. This is not a licensed engineering sign-off role.",
    bestFor: [
      "Capacity and availability assumption review",
      "Technology and operations requirements",
      "Vendor coordination",
      "Monitoring and handover readiness",
      "Executive decision support",
    ],
  },
];

export default function dataCenterReadiness() {
  // Pin the fintech tech-stack audit case study as the lead "Our Work" reference,
  // then fill the second slot with a relevant insight.
  const pinnedSlugs = ["fintech-audit-tech-stacks-strategy"];
  const pinned = pinnedSlugs
    .map(slug => blogData.find(item => item?.meta?.slug === slug))
    .filter(Boolean);

  const pool = blogData.filter(
    item => !["case-study", "tech"].includes(item?.meta?.category) && !pinnedSlugs.includes(item?.meta?.slug)
  );
  const relevant = pool.filter(i =>
    i?.meta?.refer?.some(r => ["Cloud", "Tech Consulting", "Technology Consulting", "Strategy"].includes(r))
  );
  const filler = getRandom(relevant.length ? relevant : pool, 1);

  const insights = [...pinned, ...filler];

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      {/* Hero */}
      <Service2
        heading="Data Centre Project Readiness and Delivery Governance"
        paragraph={`ITMTB helps organisations evaluate, structure, and govern data centre setup initiatives before expensive commitments are made across land, power, cooling, approvals, vendors, commissioning, and operations handover.<br/><br/>We do not position this as turnkey construction, EPC delivery, licensed engineering design, or certification delivery. Our role is to help leadership create a clearer execution path before the project becomes difficult to control.<ul style="margin-top:16px;padding-left:20px;list-style:disc;display:flex;flex-direction:column;gap:8px"><li>2 to 3 week fixed-scope readiness assessment available</li><li>Requirements, assumptions, risks, approvals, vendors, and delivery roadmap reviewed</li><li>Written risk register, work-package map, approval dependency map, and executive decision pack</li><li>PMO and owner-side technology governance support available after the assessment</li></ul>`}
        primaryCta={null}
        heroSvg={
          <svg viewBox="0 0 440 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md opacity-70" aria-hidden="true" role="presentation">
            {/* dot grid */}
            {[...Array(6)].map((_, row) =>
              [...Array(7)].map((_, col) => (
                <circle key={`${row}-${col}`} cx={40 + col * 60} cy={28 + row * 60} r="1.5" fill="#e2e8f0" />
              ))
            )}
            {/* three server racks */}
            {[96, 200, 304].map(x => (
              <g key={x}>
                <rect x={x - 34} y="150" width="68" height="150" rx="6" stroke="#94a3b8" strokeWidth="1.5" />
                {[168, 190, 212, 234, 256, 278].map(y => (
                  <line key={y} x1={x - 26} y1={y} x2={x + 26} y2={y} stroke="#cbd5e1" strokeWidth="1.2" />
                ))}
                <circle cx={x + 20} cy="160" r="2" fill="#cbd5e1" />
              </g>
            ))}
            {/* power / cooling / telecom feed lines into racks from a governance bar */}
            <rect x="61" y="96" width="318" height="34" rx="8" stroke="#94a3b8" strokeWidth="1.5" />
            <line x1="96" y1="130" x2="96" y2="150" stroke="#cbd5e1" strokeWidth="1.5" />
            <line x1="200" y1="130" x2="200" y2="150" stroke="#cbd5e1" strokeWidth="1.5" />
            <line x1="304" y1="130" x2="304" y2="150" stroke="#cbd5e1" strokeWidth="1.5" />
            {/* governance-bar node markers (power, cooling, telecom, approvals) */}
            <circle cx="110" cy="113" r="3" fill="#cbd5e1" />
            <circle cx="180" cy="113" r="3" fill="#cbd5e1" />
            <circle cx="250" cy="113" r="3" fill="#cbd5e1" />
            <circle cx="320" cy="113" r="3" fill="#cbd5e1" />
            {/* readiness scan + checkpoint accent (amber) over centre rack */}
            <rect x="166" y="143" width="68" height="164" rx="10" stroke="#b45309" strokeWidth="0.9" strokeOpacity="0.35" strokeDasharray="4 3" />
            <line x1="50" y1="225" x2="390" y2="225" stroke="#b45309" strokeWidth="1.2" strokeDasharray="6 3" strokeOpacity="0.5" />
            <circle cx="44" cy="225" r="3.5" fill="#b45309" fillOpacity="0.35" />
            <circle cx="396" cy="225" r="3.5" fill="#b45309" fillOpacity="0.35" />
            {/* decision checkmark badge */}
            <circle cx="352" cy="60" r="17" stroke="#b45309" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="4 2" />
            <circle cx="352" cy="60" r="11" stroke="#b45309" strokeWidth="1.8" fill="none" strokeOpacity="0.65" />
            <path d="M 347 60 l 3.5 3.5 l 6 -7" stroke="#b45309" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.7" />
          </svg>
        }
      />

      {/* Anchor nav */}
      <nav className="bg-white border-b border-gray-100 px-6 md:px-[60px] py-2 overflow-x-auto">
        <div className="flex gap-6 text-sm font-medium whitespace-nowrap">
          {[
            { label: "Who it's for", href: "#who-its-for" },
            { label: "What's included", href: "#whats-included" },
            { label: "Deliverables", href: "#deliverables" },
            { label: "How it works", href: "#how-it-works" },
            { label: "Engagement models", href: "#engagement-models" },
            { label: "FAQ", href: "#faq" },
          ].map(({ label, href }) => (
            <a key={href} href={href} className="text-gray-500 hover:text-indigo-600 transition-colors py-1">
              {label}
            </a>
          ))}
        </div>
      </nav>

      <CustomerSection title="Trusted by" />

      {/* § Intro */}
      <section className="px-8 md:px-[60px] py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
            Data centre projects become expensive when assumptions are not tested early
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            A data centre project is not only a building project with IT equipment inside it.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
            It is a power, cooling, telecom, civil, safety, compliance, security, vendor, commissioning, and operations-readiness programme. A weak assumption in one workstream can create cost, delay, redesign, approval risk, or handover risk later.
          </p>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-5">
            Common early assumptions that need testing
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "The selected site will support the required power load",
              "Approvals can be handled after design",
              "Cooling strategy can be decided later",
              "Fibre availability means route diversity",
              "Vendor packages will naturally integrate",
              "Commissioning can be planned at the end",
              "Operations can take over once construction is complete",
              "A concept note is enough for a board decision",
            ].map(sign => (
              <div key={sign} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">{sign}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mt-6">
            ITMTB helps project sponsors and technology leaders convert these assumptions into a written readiness view, decision roadmap, and governance structure. If the project is connected to hybrid cloud or a public cloud alternative, this often begins with the same workload and cost thinking behind our{" "}
            <Link href="/blog/cloud-price-comparison">
              <a className="text-indigo-600 hover:text-indigo-800 font-medium underline">cloud cost comparison guide</a>
            </Link>.
          </p>
        </div>
      </section>

      {/* § Who this is for */}
      <section id="who-its-for" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10">
            Who should consider this service?
          </h2>
          <div className="space-y-10">
            {[
              {
                heading: "Organisations evaluating a captive data centre or disaster recovery site",
                body: "If you are considering a captive facility, DR site, edge facility, or private infrastructure footprint, you need clarity before committing to land, design, vendors, and capital spend. We help you review whether the requirements, assumptions, dependencies, and decision gates are clear enough to proceed.",
              },
              {
                heading: "CXOs and infrastructure heads who need an independent readiness view",
                body: "Data centre plans often involve real estate, facilities, IT, finance, vendors, consultants, EPC teams, and local authorities. We help leadership see the project as an integrated delivery system, not as disconnected technical workstreams.",
              },
              {
                heading: "Companies working with EPC, MEP, or specialist vendors",
                body: "If engineering consultants and vendors are already involved, ITMTB can support the owner-side governance layer: risk tracking, workstream coordination, decision logs, approval dependencies, vendor evidence, and executive reporting. We do not replace specialist engineering partners. We help the owner govern the work more clearly.",
              },
              {
                heading: "Investors, boards, and sponsors evaluating data centre project risk",
                body: "Before approving a major commitment, sponsors need more than a high-level concept note. They need a structured view of what is known, what is assumed, what is unresolved, which approvals may matter, which vendors are needed, and what can affect schedule, cost, and go-live readiness.",
              },
              {
                heading: "Teams that need to prepare before appointing full delivery partners",
                body: "Some organisations are not yet ready to engage EPC or specialist engineering teams at full scale. A readiness assessment can help define the brief, identify missing decisions, and prepare the organisation for a better vendor selection process.",
              },
            ].map(item => (
              <div key={item.heading}>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.heading}</h3>
                <p className="text-gray-600 text-base leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* § What this service includes — Capabilities2 */}
      <div id="whats-included" className="scroll-mt-24">
        <Capabilities2
          dataList={includeAreas}
          bg="bg-[#402A6A]"
          insights={insights}
          hideFooter
          hideCardSubtitle
        />
      </div>

      {/* § Deliverables */}
      <section id="deliverables" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            What you receive
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-8">
            At the end of a readiness assessment, you receive a written report designed for both technical and business stakeholders. It is a practical decision document for project sponsors, not a generic consulting presentation.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Readiness output</p>
              <ul className="space-y-2.5">
                {[
                  "Readiness summary and assumptions register",
                  "Approval dependency map",
                  "Capacity and availability summary",
                  "Work-package and stakeholder map",
                  "Prioritised risk register",
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Decision support</p>
              <ul className="space-y-2.5">
                {[
                  "Open decision list and decision gates",
                  "30, 60, and 90 day roadmap",
                  "Recommended governance model",
                  "Specialist reviews required",
                  "Executive decision pack",
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-gray-500 text-sm mt-6 leading-relaxed">
            The goal is to help leadership decide whether to proceed, pause, revise assumptions, or structure the next phase differently.
          </p>
        </div>
      </section>

      {/* § How the assessment works */}
      <section id="how-it-works" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            How the assessment works
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            A defined five-phase process, structured to expose dependencies and risks before major commitments are made.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {engagementPhases.map(phase => (
              <div key={phase.phase} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-start gap-4 mb-5">
                  <span className="text-3xl font-bold text-indigo-200 leading-none select-none">
                    {phase.phase}
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{phase.title}</h3>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {phase.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Link href="/itmtb-how-we-work">
            <a className="mt-8 inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors">
              See the full engagement methodology →
            </a>
          </Link>
        </div>
      </section>

      {/* § Engagement models */}
      <section id="engagement-models" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-6xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Engagement models based on where the project stands
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            Start with a fixed-scope assessment, then move into governance support as the project progresses.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {engagementModels.map(model => (
              <div key={model.label} className="bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-col">
                <div className="mb-4">
                  <h3 className="text-base font-bold text-gray-900 mb-1">{model.label}</h3>
                  <p className="text-xs font-semibold text-indigo-500 uppercase tracking-wide">{model.duration}</p>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">{model.description}</p>
                <div className="mt-auto">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Best for</p>
                  <ul className="space-y-2">
                    {model.bestFor.map(item => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* § What we do not do */}
      <section className="px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            What this service does not do
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-8">
            This service is intentionally limited. Where specialist sign-off is required, we work with or recommend qualified engineering, legal, regulatory, fire, electrical, environmental, EPC, or certification partners. Our role is readiness, governance, technology-side coordination, risk visibility, and owner-side decision support.
          </p>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-5">
            ITMTB does not provide
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Licensed MEP engineering design",
              "Electrical, civil, or structural sign-off",
              "Fire-safety certification",
              "Statutory approval filing as a licensed authority",
              "EPC or turnkey construction delivery",
              "Tier or TIA-942 certification",
              "Guaranteed approvals or sanctioned load",
              "Guaranteed project timelines",
            ].map(item => (
              <div key={item} className="flex items-start gap-3 px-1 py-1">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* § Where specialist partners fit */}
      <section className="px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Where specialist partners fit
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            Data centre projects require specialist expertise. Depending on the project, you may need MEP consultants, electrical engineers, fire consultants, civil and structural engineers, environmental consultants, regulatory consultants, EPC partners, telecom providers, certification advisors, and operations and maintenance partners.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            ITMTB coordinates the readiness and governance layer around these partners. Final engineering, statutory, regulatory, and certification sign-offs must come from qualified specialists.
          </p>
        </div>
      </section>

      {/* § Related capabilities */}
      <section className="px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-7">
            Related capabilities
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                href: "/services/technology-consulting",
                label: "Technology Consulting",
                desc: "Independent technical due diligence, roadmap development, delivery governance, and project decision support.",
              },
              {
                href: "/services/tech-stack-audit",
                label: "Tech Stack Audit",
                desc: "Use this when the concern is an existing software, cloud, database, security, or operations stack rather than a physical data centre project.",
              },
              {
                href: "/services/cloud",
                label: "Cloud Infrastructure",
                desc: "Use this when the data centre decision is connected to hybrid cloud, migration, cloud cost, resiliency, or architecture planning.",
              },
              {
                href: "/services/managed-services",
                label: "Managed Services",
                desc: "Use this when operational ownership, monitoring, reporting, escalation, and ongoing support are needed after a system is live.",
              },
              {
                href: "/services/cybersecurity",
                label: "Cybersecurity",
                desc: "Use this when physical infrastructure planning needs to align with access control, monitoring, cyber controls, and security governance.",
              },
              {
                href: "/services/strategy",
                label: "Strategy",
                desc: "Business-aligned technology strategy and roadmaps for organisations planning their next phase of infrastructure growth.",
              },
            ].map(link => (
              <Link key={link.href} href={link.href}>
                <a className="block bg-white border border-gray-200 rounded-xl p-6 hover:border-indigo-400 hover:shadow-sm transition-all group">
                  <h3 className="text-base font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors mb-2">
                    {link.label}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{link.desc}</p>
                  <span className="mt-4 inline-block text-sm font-semibold text-indigo-500 group-hover:text-indigo-700 transition-colors">
                    Learn more →
                  </span>
                </a>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div id="faq" className="scroll-mt-24">
        <ServiceFAQ items={faqItems} />
      </div>

      <ServiceEngagementLinks />

      {/* Final CTA */}
      <section className="px-8 md:px-[60px] py-16 bg-[#0d0822]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-5">
            Start the conversation
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-5">
            Get a clearer view before committing to a data centre project
          </h2>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-8">
            Before you commit to land, vendors, design, approvals, or major procurement, understand the assumptions and risks behind the project. ITMTB helps leadership create a practical readiness view, risk register, approval dependency map, work-package map, and delivery roadmap.
          </p>
          <Link href="/contact-us?utm_source=service-data-center-readiness&utm_content=bottom-cta">
            <a className="inline-block bg-[#f1aa4e] text-black font-semibold text-base px-8 py-3.5 rounded-md hover:bg-[#e09a3e] transition-colors">
              Request a Readiness Assessment →
            </a>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
